// Hilfsfunktionen für Sitz-Validierung im Shop-System

import { State } from "@store/state";
import { Product, Price } from '../../../app/graphql/codegen';

/**
 * Checks if a price is a seat price based on seatType field
 */
export const isSeatPrice = (price: Price): boolean => {
  return price.seatType !== null && price.seatType !== undefined;
};

/**
 * Gets the seat type from a price
 */
export const getSeatTypeFromPrice = (price: Price): 'S' | 'M' | 'L' | null => {
  if (!isSeatPrice(price)) return null;
  return price.seatType as 'S' | 'M' | 'L';
};

/**
 * Checks if a product has any seat prices
 */
export const hasAnySeatPrices = (product: Product): boolean => {
  return product.prices.some(price => price && isSeatPrice(price));
};

/**
 * Gets all seat prices from a product
 */
export const getSeatPricesFromProduct = (product: Product): Price[] => {
  return product.prices.filter((price): price is Price => price !== null && isSeatPrice(price));
};

/**
 * Finds a seat price by type from a list of products
 */
export const findSeatPriceByType = (products: Product[], seatType: 'S' | 'M' | 'L'): Price | null => {
  for (const product of products) {
    const seatPrice = product.prices.find(price => 
      price && price.seatType === seatType
    );
    if (seatPrice) return seatPrice;
  }
  return null;
};

/**
 * Gets all seat prices from a list of products, organized by type
 */
export const getAllSeatPrices = (products: Product[]): { S: Price | null; M: Price | null; L: Price | null } => {
  return {
    S: findSeatPriceByType(products, 'S'),
    M: findSeatPriceByType(products, 'M'),
    L: findSeatPriceByType(products, 'L'),
  };
};

/**
 * Validiert, ob mindestens ein Sitz im aktuellen Shop-Zustand ausgewählt ist
 * @param selectedProducts Aktuell ausgewählte Produkte
 * @param products Verfügbare Produkte als Referenz
 * @returns True wenn mindestens ein Sitz ausgewählt ist
 */
export const hasAtLeastOneSeatSelected = (
    selectedProducts: Array<{ priceId: string; quantity: number }>,
    products: Product[]
): boolean => {
    // Flatten all prices from all products
    const allPrices = products.flatMap(product => product.prices);
    
    for (const selectedProduct of selectedProducts) {
        if (selectedProduct.quantity > 0) {
            // Find the price object for this priceId
            const price = allPrices.find(p => p && p.priceId === selectedProduct.priceId);
            
            // Check if this price is a seat price using the new seatType field
            if (price && isSeatPrice(price)) {
                return true;
            }
        }
    }
    return false;
};
